import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import Paper from '@aura/components/paper'
import { withStyles } from '@aura/components/styles'
import { useAuraTheme } from '@aura/components/themes'
import Typography from '@aura/components/typography'
import { Checkbox as MuiCheckbox } from '@mui/material'
import TextField from '@aura/components/text-field'
import Autocomplete from '@aura/components/autocomplete'
import clsx from 'clsx'
import Form from '@aura/forms/form'
import Button from '@aura/components/button'
import Box from '@aura/components/box'
import Grid from '@aura/components/grid'
import IconButton from '@aura/components/icon-button'
import { useTranslation } from 'react-i18next'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EditIcon from '@mui/icons-material/Edit'
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ReCAPTCHA from 'react-google-recaptcha'
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Tooltip } from '@mui/material'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import appContext from '../../../../utils/app-context'
import constants from '../../../../constants/languages'
import languageList from '../../../../constants/language-list'
import Footer from '../../../../components/footer-new'
import AppBarTool from '../../../../components/app-bar-tool'
import SubmitSuccess from '../successSubmit'
import MenuContext from '../../../../utils/menu-context'
import { isFrench } from '../../../../constants/app'
import formFields, { initialValues } from './constants/form-fields'
import RequestSubmits from '../../../../constants/request-submit'
import styles, { REGISTER_SCROLLBAR_STYLES } from './styles'


const { EMAIL, FIRST_NAME, LAST_NAME, COMPANY_NAME, PHONE_NUMBER, LANGUAGE, COUNTRY_CODE, EXT } = formFields

const NO_DATA = 'registrationAnalytics.noData'
const SE_APP_NAME = 'Structured Edge'
const MATA_APP_NAME = 'Multi-Asset Trade Analytics'
const MAX_PROFILE_TEXT_LENGTH = 255
const DEALER_CODE_REGEX = /^\d{4}$/
const REP_CODE_REGEX = /^[A-Za-z0-9]{2,5}$/
const FUNDSERV_REGISTRY_URL_EN = 'https://www.fundserv.com/industry-resources/member-registry/'
const FUNDSERV_REGISTRY_URL_FR = 'https://www.fundserv.com/fr/industry-resources-francais/registre-des-membres/'

let rowCounter = 0
const buildRow = () => ({
    dealerCode: '',
    id: `dr-row-${Date.now()}-${rowCounter++}`,
    repCodes: [],
    repCodesInput: '',
})

const validateRows = (rows, translate) => {
    const seenDealerCodes = new Set()
    return rows.map(row => {
        const dealerCode = (row.dealerCode || '').trim()
        const repCodes = row.repCodes || []
        const isDuplicate = dealerCode && seenDealerCodes.has(dealerCode)
        if (dealerCode) seenDealerCodes.add(dealerCode)

        return {
            id: row.id,
            dealerCode: !dealerCode ? translate('requestAccess.dealerCodeRequired')
                : !DEALER_CODE_REGEX.test(dealerCode) ? translate('requestAccess.dealerCodeInvalid')
                    : isDuplicate ? translate('requestAccess.dealerCodeUnique') : '',
            repCodes: repCodes.length === 0 ? translate('requestAccess.repCodesRequired')
                : !repCodes.every(c => REP_CODE_REGEX.test(c)) ? translate('requestAccess.repCodesInvalid') : ''
        }
    })
}

const Stepper = ({ classes, page, stepOneLabel, stepTwoLabel, stepThreeLabel }) => {
    const isStepOne = page >= 1
    const isStepTwo = page >= 2
    const isStepThree = page === 3

    return (
        <Box className={classes.stepperWrapper}>
            <Box className={classes.stepItem}>
                <Box className={clsx(classes.stepperCircle, isStepOne && classes.stepperCircleActive)}>
                    {isStepOne ? <CheckIcon className={classes.stepCheckIcon} /> : '1'}
                </Box>
                <span className={clsx(classes.stepLabel, !isStepOne && classes.stepLabelInactive)}>{stepOneLabel}</span>
            </Box>
            <Box className={classes.stepDivider} />
            <Box className={classes.stepItem}>
                <Box className={clsx(classes.stepperCircle, isStepTwo && classes.stepperCircleActive)}>
                    {isStepTwo ? <CheckIcon className={classes.stepCheckIcon} /> : <span className={classes.stepperCircleNumber}>2</span>}
                </Box>
                <span className={clsx(classes.stepLabel, !isStepTwo && classes.stepLabelInactive)}>{stepTwoLabel}</span>
            </Box>
            <Box className={classes.stepDivider} />
            <Box className={classes.stepItem}>
                <Box className={clsx(classes.stepperCircle, isStepThree && classes.stepperCircleActive)}>
                    {isStepThree ? <CheckIcon className={classes.stepCheckIcon} /> : <span className={classes.stepperCircleNumber}>3</span>}
                </Box>
                <span className={clsx(classes.stepLabel, !isStepThree && classes.stepLabelInactive)}>{stepThreeLabel}</span>
            </Box>
        </Box>
    )
}

const Component = ({
    classes,
    errorMessage,
    onAddNotification,
    onRequestApplication,
    onRequestPhoneCodes,
    onRequestRecaptcha,
    onSubmitRequest,
    phoneCodes,
    recaptchaValue,
    requestApplications,
    stepNumber,
    submitStatus
}) => {

    const { t: translate, i18n } = useTranslation()
    const { mode: auraMode } = useAuraTheme()
    const isDarkMode = auraMode === 'dark'
    const stepOneLabel = translate('requestAccess.stepSelectProducts')
    const stepTwoLabel = translate('requestAccess.stepEnterInformation')
    const stepThreeLabel = translate('requestAccess.stepReviewSubmit')
    const moveToTopRef = useRef(null)
    const ref = useRef()
    const recaptchaRef = useRef(null)
    const scrollContainerRef = useRef(null)
    const cardBodyRef = useRef(null)
    const emailBlurredRef = useRef(false)
    const emailFocusedRef = useRef(false)
    const { locale } = useParams()
    const history = useHistory()
    const menuContext = useContext(MenuContext)
    const [ selectedLanguage, setSelectedLanguage ] = useState(i18n.language)
    const [ formInfoValues, setFormInfoValues ] = useState({ ...initialValues })
    const [ hasVisibleFormErrors, setHasVisibleFormErrors ] = useState(false)
    const [ showFormBlockedMessage, setShowFormBlockedMessage ] = useState(false)
    const memoizedInitialValues = useMemo(() => formInfoValues, [ formInfoValues ])
    const { mobileDevice } = menuContext
    const isMobile = window.innerWidth <= 670
    const smallScreen = window.innerWidth > 671 && window.innerWidth <= 1024
    const defaultPath = window.location.pathname.split('/')[0] + '/register'
    const previousPath = window.location.pathname.split('/register')[0] + '/login'
    const { application: applicationContext } = useContext(appContext)
    const [ page, setPage ] = useState(1)
    const [ returningFromEdit, setReturningFromEdit ] = useState(false)
    const [ applications, setApplications ] = useState([])
    const [ selectedAppIds, setSelectedAppIds ] = useState([])

    // Multi-Asset Trade Analytics checkbox state
    const [ mataSelectedOptions, setMataSelectedOptions ] = useState([])

    // Dealer / rep code state
    const [ rows, setRows ] = useState([ buildRow() ])
    const [ rowErrors, setRowErrors ] = useState([ { id: rows[0].id, dealerCode: '', repCodes: '' } ])
    const [ forceRowErrors, setForceRowErrors ] = useState(false)
    const [ touchedRows, setTouchedRows ] = useState([ { dealer: false, reps: false } ])

    // Country code state
    const [ countryCodeList, setCountryCodeList ] = useState([])
    const fundservRegistryUrl = isFrench(selectedLanguage) ? FUNDSERV_REGISTRY_URL_FR : FUNDSERV_REGISTRY_URL_EN

    // reCAPTCHA state
    const [ reCaptcha, setReCaptcha ] = useState(null)
    const [ captchaError, setCaptchaError ] = useState('')
    const [ siteKey, setSiteKey ] = useState(null)
    const [ recaptchaKey, setRecaptchaKey ] = useState(0)

    const getOptionLabel = option => option ? translate('language.' + option.label) : ''
    const getOptionLabelList = (option, list) => {
        if (!option || !list) return ''
        const match = list.find(rec => rec.value === (option.value || option))
        return match ? match.label : ''
    }

    const updateVisibleFormErrors = useCallback((fromSubmit = false) => {
        const ctx = ref.current?.getContext?.()
        const errors = ctx?.errors || {}
        const touched = ctx?.touched || {}
        const hasVisible = Object.keys(errors).some(field => Boolean(errors[field]) && Boolean(touched[field]))
        setHasVisibleFormErrors(hasVisible)
        if (!hasVisible) setShowFormBlockedMessage(false)
        if (fromSubmit && hasVisible) setShowFormBlockedMessage(true)
    }, [])

    const handleSubmitButtonClick = useCallback(() => {
        emailBlurredRef.current = true
        emailFocusedRef.current = false
        const ctx = ref.current?.getContext?.()
        if (ctx?.setTouched) {
            const allTouched = Object.values(formFields).reduce((acc, field) => {
                acc[field.value] = true
                return acc
            }, {})
            ctx.setTouched(allTouched, false)
        }
        setTimeout(() => updateVisibleFormErrors(true), 0)
    }, [ updateVisibleFormErrors ])

    const clearFieldError = useCallback(fieldName => {
        const ctx = ref.current?.getContext?.()
        if (ctx?.setFieldError) ctx.setFieldError(fieldName, undefined)
    }, [])

    const handleFieldBlur = useCallback(() => {
        const ctx = ref.current?.getContext?.()
        if (ctx?.validateForm) ctx.validateForm().then(() => updateVisibleFormErrors())
    }, [ updateVisibleFormErrors ])

    useEffect(() => {
        const defaultLang = constants.ENGLISH
        if (locale === constants.ENGLISH || locale === constants.FRENCH) {
            setSelectedLanguage(locale)
            i18n.changeLanguage(locale)
        } else if (isFrench(selectedLanguage)) {
            setSelectedLanguage(constants.FRENCH)
            i18n.changeLanguage(constants.FRENCH)
            history.push(defaultPath + `/${constants.FRENCH}`)
        } else {
            setSelectedLanguage(defaultLang)
            i18n.changeLanguage(defaultLang)
            history.push(defaultPath + `/${defaultLang}`)
        }
    }, [ locale ])

    useEffect(() => {
        onRequestApplication()
        onRequestPhoneCodes()
        onRequestRecaptcha()
    }, [])

    useEffect(() => {
        if (requestApplications) {
            const appList = Object.values(requestApplications)
            setApplications(appList)
            setSelectedAppIds([])
        }
    }, [ requestApplications ])

    useEffect(() => {
        if (phoneCodes && phoneCodes.length > 0) {
            const formatted = phoneCodes.map(code => {
                const [ country, ...rest ] = code.label.split(' ')
                return { ...code, label: `${rest.join(' ')} ${country}` }
            })
            setCountryCodeList(formatted)
        }
    }, [ phoneCodes ])

    useEffect(() => {
        const styleEl = document.createElement('style')
        styleEl.setAttribute('data-register-scrollbars', 'true')
        styleEl.innerHTML = REGISTER_SCROLLBAR_STYLES
        document.head.appendChild(styleEl)

        const el = scrollContainerRef.current
        let scrollTimer = null

        const onScroll = () => {
            if (!el) return
            el.classList.add('is-scrolling')
            clearTimeout(scrollTimer)
            scrollTimer = setTimeout(() => {
                el.classList.remove('is-scrolling')
            }, 1000)
        }

        if (el) {
            el.addEventListener('scroll', onScroll, { passive: true })
        }

        return () => {
            if (el) {
                el.removeEventListener('scroll', onScroll)
            }
            clearTimeout(scrollTimer)
            document.head.removeChild(styleEl)
        }
    }, [])

    useEffect(() => {
        const el = cardBodyRef.current
        if (!el) return
        let timer = null
        const onScroll = () => {
            el.classList.add('is-scrolling')
            clearTimeout(timer)
            timer = setTimeout(() => el.classList.remove('is-scrolling'), 1000)
        }
        el.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            el.removeEventListener('scroll', onScroll)
            clearTimeout(timer)
        }
    }, [ page, stepNumber ])

    useEffect(() => {
        setTouchedRows(prev => rows.map((_, index) => prev[index] || { dealer: false, reps: false }))
        setRowErrors(validateRows(rows, translate))
    }, [ rows, translate ])

    useEffect(() => {
        const ctx = ref.current?.getContext?.()
        if (ctx?.validateForm) {
            Promise.resolve(ctx.validateForm()).finally(updateVisibleFormErrors)
        }
    }, [ i18n.language, updateVisibleFormErrors ])

    useEffect(() => {
        if (recaptchaValue?.site_key) {
            setSiteKey(recaptchaValue.site_key)
            setReCaptcha(null)
            setRecaptchaKey(prev => prev + 1)
        }
    }, [ recaptchaValue ])

    const onReCaptchaHandle = val => {
        if (val) {
            setReCaptcha(val)
            setCaptchaError('')
        }
    }

    const handleMataOptionToggle = useCallback(value => {
        setMataSelectedOptions(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [ ...prev, value ]
        )
    }, [])

    const handleToggleApp = useCallback(appId => {
        const id = String(appId)
        setSelectedAppIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [ ...prev, id ]
        )
    }, [])

    const handleDealerChange = useCallback((index, value) => {
        const sanitized = value.replace(/\D/g, '')
        setRows(prev => {
            const next = prev.map((row, i) => i === index ? { ...row, dealerCode: sanitized } : row)
            setRowErrors(validateRows(next, translate))
            return next
        })
    }, [ translate ])

    const handleRepChange = useCallback((index, values) => {
        const normalized = Array.from(new Set((values || []).map(option => {
            if (typeof option === 'string') return option.trim().toUpperCase()
            return (option?.value || option?.label || '').toUpperCase()
        }).filter(Boolean)))
        const filtered = normalized.filter(code => REP_CODE_REGEX.test(code))
        setRows(prev => {
            const next = prev.map((row, i) => i === index ? { ...row, repCodes: filtered } : row)
            setRowErrors(validateRows(next, translate))
            return next
        })
    }, [ translate ])

    const handleRepInputChange = useCallback((index, inputValue, reason) => {
        setRows(prev => prev.map((row, i) => {
            if (i !== index) return row

            const hasInvalidPendingCode = (row.repCodesInput || '')
                .split(/[\s,]+/)
                .map(code => code.trim())
                .filter(Boolean)
                .some(code => !REP_CODE_REGEX.test(code))

            if (reason === 'reset' && hasInvalidPendingCode) {
                return row
            }

            const sanitizedInput = (inputValue || '')
                .toUpperCase()
                .replace(/[^A-Z0-9,\s]/g, '')

            return { ...row, repCodesInput: sanitizedInput }
        }))
    }, [])

    const commitRepInput = useCallback(index => {
        setRows(prev => {
            const current = prev[index]
            if (!current) return prev

            const pending = (current.repCodesInput || '').trim().toUpperCase()
            if (!pending) return prev

            const pendingCodes = pending
                .split(/[\s,]+/)
                .map(code => code.trim())
                .filter(Boolean)

            const hasInvalidPendingCode = pendingCodes.some(code => !REP_CODE_REGEX.test(code))
            if (hasInvalidPendingCode) {
                return prev
            }

            const validCodes = pendingCodes.filter(code => REP_CODE_REGEX.test(code))

            if (validCodes.length === 0) {
                return prev
            }

            const merged = Array.from(new Set([ ...(current.repCodes || []), ...validCodes ]))
            const next = prev.map((row, i) => i === index ? { ...row, repCodes: merged, repCodesInput: '' } : row)
            setRowErrors(validateRows(next, translate))
            return next
        })
    }, [ translate ])

    const handleAddRow = useCallback(() => {
        const newRow = buildRow()
        setForceRowErrors(false)
        setRows(prev => {
            const next = [ ...prev, newRow ]
            setRowErrors(validateRows(next, translate))
            return next
        })
    }, [ translate ])

    const handleRemoveRow = useCallback(index => {
        setRows(prev => {
            if (prev.length === 1) return prev
            const next = prev.filter((_, i) => i !== index)
            setRowErrors(validateRows(next, translate))
            return next
        })
        onAddNotification({
            message: translate('requestAccess.dealerRepRowRemoved'),
            type: 'success'
        })
    }, [ onAddNotification, translate ])

    const markDealerTouched = useCallback(index => {
        setTouchedRows(prev => rows.map((_, i) => {
            const current = prev[i] || { dealer: false, reps: false }
            return i === index ? { ...current, dealer: true } : current
        }))
    }, [ rows ])

    const markRepTouched = useCallback(index => {
        setTouchedRows(prev => rows.map((_, i) => {
            const current = prev[i] || { dealer: false, reps: false }
            return i === index ? { ...current, reps: true } : current
        }))
    }, [ rows ])

    const handleRepBlur = useCallback(index => {
        commitRepInput(index)
        markRepTouched(index)
    }, [ commitRepInput, markRepTouched ])

    const handleRepKeyDown = useCallback((event, index) => {
        const key = event.key
        if (key === 'Enter' || key === 'Escape' || key === ' ' || key === 'Spacebar') {
            const current = rows[index]
            const pendingCodes = (current?.repCodesInput || '')
                .split(/[\s,]+/)
                .map(code => code.trim())
                .filter(Boolean)
            const hasInvalidPendingCode = pendingCodes.some(code => !REP_CODE_REGEX.test(code))

            event.preventDefault()

            if (hasInvalidPendingCode) {
                event.stopPropagation()
                if (event.nativeEvent?.stopImmediatePropagation) {
                    event.nativeEvent.stopImmediatePropagation()
                }
                event.defaultMuiPrevented = true
                markRepTouched(index)
                return
            }

            commitRepInput(index)
            markRepTouched(index)
        }
    }, [ commitRepInput, markRepTouched, rows ])

    const seSelected = applications.some(
        app => app.name === SE_APP_NAME && selectedAppIds.includes(String(app.id))
    )

    const mataSelected = applications.some(
        app => app.name === MATA_APP_NAME && selectedAppIds.includes(String(app.id))
    )

    const hasPendingRepInputErrors = seSelected && rows.some(row => (row.repCodesInput || '')
        .split(/[\s,]+/)
        .map(code => code.trim())
        .filter(Boolean)
        .some(code => !REP_CODE_REGEX.test(code)))

    const hasStepOneInteraction = forceRowErrors || touchedRows.some(t => t?.dealer || t?.reps)

    const hasStepOneValidationErrors = seSelected
        && hasStepOneInteraction
        ? validateRows(rows, translate).some(e => e.dealerCode || e.repCodes)
        : false

    const isStepOneNextDisabled = hasPendingRepInputErrors || hasStepOneValidationErrors

    const showStepOneBlockedMessage = forceRowErrors && (hasPendingRepInputErrors || hasStepOneValidationErrors)

    const getDialCodeFromCountryValue = useCallback(selectedCountryCode => {
        if (selectedCountryCode?.value) {
            return selectedCountryCode.value
        }

        if (typeof selectedCountryCode === 'string') {
            const matchedCode = countryCodeList.find(code => code.value === selectedCountryCode || code.label === selectedCountryCode)
            if (matchedCode?.value) {
                return matchedCode.value
            }
        }

        const defaultCode = countryCodeList.find(code => code.value === '+1')
        return defaultCode?.value || ''
    }, [ countryCodeList ])

    const hasRequiredFieldsFilled = useMemo(() => {
        const vals = formInfoValues
        return !Object.values(formFields).some(field => field.isRequired && !vals[field.value])
    }, [ formInfoValues ])

    const isFormValid = useMemo(() => {
        const vals = formInfoValues
        const missingRequired = Object.values(formFields).some(field => field.isRequired && !vals[field.value])
        if (missingRequired) return false
        if ((vals[FIRST_NAME.value] || '').length > MAX_PROFILE_TEXT_LENGTH) return false
        if ((vals[LAST_NAME.value] || '').length > MAX_PROFILE_TEXT_LENGTH) return false
        if ((vals[COMPANY_NAME.value] || '').length > MAX_PROFILE_TEXT_LENGTH) return false
        if (vals[EMAIL.value] && !/^[a-z0-9!'#$%&*+/=?^_`{|}~-]+(?:\.[a-z0-9!'#$%&*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-zA-Z]{2,}$/i.test(vals[EMAIL.value])) return false
        if (vals[EMAIL.value]) {
            const emailLower = vals[EMAIL.value].toLowerCase()
            const domainPart = emailLower.split('@')[1] || ''
            const exactDomains = [ 'rbc.com', 'rbccm.com', 'sterbc.com', 'sterbccm.com', 'istrbc.com', 'istrbccm.com' ]
            if (exactDomains.some(domain => emailLower.endsWith(`@${domain}`))) return false
        }
        if (vals[PHONE_NUMBER.value]) {
            const dialCode = getDialCodeFromCountryValue(vals[COUNTRY_CODE.value])
            const phone = parsePhoneNumberFromString(dialCode + vals[PHONE_NUMBER.value])
            if (!phone || !phone.isValid()) return false
        }
        return true
    }, [ formInfoValues, getDialCodeFromCountryValue ])

    const handleNext = () => {
        if (hasPendingRepInputErrors) {
            setForceRowErrors(true)
            return
        }

        if (seSelected) {
            const rowValidation = validateRows(rows, translate)
            setRowErrors(rowValidation)
            const hasRowErrors = rowValidation.some(e => e.dealerCode || e.repCodes)
            if (hasRowErrors) {
                setForceRowErrors(true)
                return
            }
        }
        setForceRowErrors(false)
        if (returningFromEdit) {
            setPage(3)
        } else {
            setPage(2)
        }
    }

    const handleBack = () => {
        const ctx = ref.current?.getContext?.()
        if (ctx?.values) {
            setFormInfoValues(ctx.values)
        }
        setPage(1)
    }

    const [ cancelDialogOpen, setCancelDialogOpen ] = useState(false)

    const handleCancel = useCallback(() => {
        setCancelDialogOpen(true)
    }, [])

    const handleCancelDialogBack = useCallback(() => {
        setCancelDialogOpen(false)
    }, [])

    const handleCancelDialogConfirm = useCallback(() => {
        setCancelDialogOpen(false)
        history.push(previousPath)
    }, [ previousPath ])

    const handlePhoneNumberChange = e => {
        const { setFieldValue, values } = ref.current.getContext()
        const val = /^$|^[0-9-()]+$/.test(e.target.value) ? e.target.value : values[PHONE_NUMBER.value]
        setFieldValue(PHONE_NUMBER.value, val)
    }

    const handleExtChange = e => {
        const { setFieldValue, values } = ref.current.getContext()
        const val = /^$|^[0-9]{0,32}$/.test(e.target.value) ? e.target.value : values[EXT.value]
        setFieldValue(EXT.value, val)
    }

    const validate = values => {
        const errors = {}

        Object.values(formFields).forEach(field => {
            if (!values[field.value] && field.isRequired) {
                errors[field.value] = translate('required')
            }
        })

        if ((values[FIRST_NAME.value] || '').length > MAX_PROFILE_TEXT_LENGTH) {
            errors[FIRST_NAME.value] = translate('requestAccess.maxLength255')
        }

        if ((values[LAST_NAME.value] || '').length > MAX_PROFILE_TEXT_LENGTH) {
            errors[LAST_NAME.value] = translate('requestAccess.maxLength255')
        }

        if ((values[COMPANY_NAME.value] || '').length > MAX_PROFILE_TEXT_LENGTH) {
            errors[COMPANY_NAME.value] = translate('requestAccess.maxLength255')
        }


        if (emailBlurredRef.current && !emailFocusedRef.current && values[EMAIL.value] && !/^[a-z0-9!'#$%&*+/=?^_`{|}~-]+(?:\.[a-z0-9!'#$%&*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-zA-Z]{2,}$/i.test(values[EMAIL.value])) {
            errors[EMAIL.value] = translate('incorrectEmail')
        }

        if (emailBlurredRef.current && !emailFocusedRef.current && values[EMAIL.value] && !errors[EMAIL.value]) {
            const emailLower = values[EMAIL.value].toLowerCase()
            const domainPart = emailLower.split('@')[1] || ''
            const exactDomains = [ 'rbc.com', 'rbccm.com', 'sterbc.com', 'sterbccm.com', 'istrbc.com', 'istrbccm.com' ]
            if (exactDomains.some(domain => emailLower.endsWith(`@${domain}`))) {
                errors[EMAIL.value] = translate('rbcEmail')
            }
        }

        if (values[PHONE_NUMBER.value]) {
            const dialCode = getDialCodeFromCountryValue(values[COUNTRY_CODE.value])
            const phone = parsePhoneNumberFromString(dialCode + values[PHONE_NUMBER.value])
            if (!phone || !phone.isValid()) {
                errors[PHONE_NUMBER.value] = translate('incorrectPhoneNumber')
            }
        }

        if (!values[LANGUAGE.value]) {
            errors[LANGUAGE.value] = translate('incorrectlanguage')
        }

        return errors
    }

    const handleSubmit = values => {
        setFormInfoValues(values)
        setReCaptcha(null)
        setCaptchaError('')
        setRecaptchaKey(prev => prev + 1)
        setPage(3)
    }

    const handleFinalSubmit = () => {
        if (siteKey && !reCaptcha) {
            setCaptchaError(translate('recaptchaError'))
            return
        }
        setCaptchaError('')
        const values = formInfoValues
        const prefix = getDialCodeFromCountryValue(values[COUNTRY_CODE.value])
        const phoneNumber = values.ext
            ? prefix + values.phoneNumber + 'x' + values.ext
            : prefix + values.phoneNumber

        const appRequestData = {}
        if (seSelected) {
            const seApp = applications.find(app => app.name === SE_APP_NAME)
            if (seApp) appRequestData[String(seApp.id)] = rows.map(row => ({ dealerCode: row.dealerCode, repCodes: row.repCodes }))
        }
        if (mataSelected && mataSelectedOptions.length > 0) {
            const mataApp = applications.find(app => app.name === MATA_APP_NAME)
            if (mataApp) appRequestData[String(mataApp.id)] = mataSelectedOptions
        }

        onSubmitRequest({
            companyName: values.companyName,
            email: values.email,
            firstName: values.firstName,
            interestedIn: selectedAppIds.map(id => parseInt(id, 10)),
            lastName: values.lastName,
            phoneNumber,
            preferredLanguage: values.preferredLanguage?.value || values.preferredLanguage,
            ...(Object.keys(appRequestData).length > 0 && { appRequestData }),
        })
    }

    const handleBackFromReview = () => {
        setPage(2)
        setReturningFromEdit(false)
        setReCaptcha(null)
        setCaptchaError('')
    }

    return (
        <Box className={classes.pageWrapper}>
            <AppBarTool />

            <Box ref={scrollContainerRef} data-register-scroll-container className={classes.scrollContainer}>
                <Box style={cancelDialogOpen ? { filter: 'blur(6px)', transition: 'filter 0.2s ease' } : { transition: 'filter 0.2s ease' }}>
                <Box ref={moveToTopRef} />
                <Grid container className={clsx(classes.gridContainerCenter, classes.gridContainerFill)}>
                    <Grid className={clsx(classes.gridContainerPadding, stepNumber === 1 && classes.gridContainerPaddingSuccess)} md={10} sm={11} xs={12}>

                        {stepNumber === 0 && <Stepper classes={classes} page={page} stepOneLabel={stepOneLabel} stepTwoLabel={stepTwoLabel} stepThreeLabel={stepThreeLabel} />}

                        {stepNumber === 0 && page === 1 && (
                            <Paper className={classes.productContentContainer}>
                                <Box className={classes.cardHeader}>
                                    <Typography className={classes.title}>{stepOneLabel}</Typography>
                                </Box>
                                <Box ref={cardBodyRef} className={classes.cardBody} data-inner-scroll>
                                    <Typography className={classes.reviewSubtitle}>
                                        {translate('requestAccess.selectProductsSubtitle')}
                                    </Typography>
                                    {applications.map(app => {
                                        const isChecked = selectedAppIds.includes(String(app.id))
                                        const name = isFrench(selectedLanguage) ? (app.nameFr || app.name) : app.name
                                        const description = isFrench(selectedLanguage) ? (app.descriptionFr || app.description || '') : (app.description || '')
                                        return (
                                            <Box key={app.id} className={classes.productCard}>
                                                <Box className={classes.productCardHeader}>
                                                    <Box className={classes.productCardContent}>
                                                        <Typography className={classes.productCardTitle}>
                                                            {name}
                                                        </Typography>
                                                        {description && (
                                                            <Typography className={classes.productCardDescription}>
                                                                {description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <MuiCheckbox
                                                        checked={isChecked}
                                                        onChange={() => handleToggleApp(app.id)}
                                                        className={classes.productCheckbox}
                                                    />
                                                </Box>

                                                {isChecked && app.name === MATA_APP_NAME && app.requestAccessData?.type === 'checkbox' && (
                                                    <>
                                                        <Divider className={classes.sectionDivider} />
                                                        <Typography className={clsx(classes.instructionText, classes.instructionTextTight)}>
                                                            {translate('requestAccess.mataCapabilitiesLabel')}
                                                        </Typography>
                                                        <Box className={classes.mataCheckboxRow}>
                                                            {(app.requestAccessData.values || []).map((enOption, idx) => {
                                                                const displayLabel = isFrench(selectedLanguage)
                                                                    ? ((app.requestAccessData.valuesFr || app.requestAccessData.values)[idx] || enOption)
                                                                    : enOption
                                                                return (
                                                                    <Box key={enOption} className={classes.mataCheckboxItem}>
                                                                        <MuiCheckbox
                                                                            checked={mataSelectedOptions.includes(enOption)}
                                                                            onChange={() => handleMataOptionToggle(enOption)}
                                                                            size="small"
                                                                        />
                                                                        <Typography className={classes.instructionText}>{displayLabel}</Typography>
                                                                    </Box>
                                                                )
                                                            })}
                                                        </Box>
                                                    </>
                                                )}

                                                {isChecked && app.name === SE_APP_NAME && (
                                                    <>
                                                        <Divider className={classes.sectionDivider} />
                                                        <Typography className={clsx(classes.instructionText, classes.instructionTextTight)}>
                                                            {translate('requestAccess.dealerRepInstruction1')}
                                                        </Typography>
                                                        {isFrench(selectedLanguage) ? (
                                                            <Typography className={clsx(classes.instructionText, classes.instructionTextLoose)}>
                                                                {translate('requestAccess.dealerRepInstruction2')}{' '}
                                                                {translate('requestAccess.dealerRepInstruction3')}{' '}
                                                                <a href={fundservRegistryUrl} target="_blank" rel="noopener noreferrer" className={classes.externalLink}>
                                                                    {translate('requestAccess.fundservLink')}
                                                                    <OpenInNewIcon className={classes.externalLinkIcon} />
                                                                </a>
                                                            </Typography>
                                                        ) : (
                                                            <>
                                                                <Typography className={classes.instructionText}>
                                                                    {translate('requestAccess.dealerRepInstruction2')}
                                                                </Typography>
                                                                <Typography className={clsx(classes.instructionText, classes.instructionTextLoose)}>
                                                                    {translate('requestAccess.dealerRepInstruction3')}{' '}<a href={fundservRegistryUrl} target="_blank" rel="noopener noreferrer" className={classes.externalLink}>
                                                                        {translate('requestAccess.fundservLink')}
                                                                        <OpenInNewIcon className={classes.externalLinkIcon} />
                                                                    </a>
                                                                </Typography>
                                                            </>
                                                        )}

                                                        <Box className={classes.rowContainer}>
                                                            {rows.map((row, index) => {
                                                                const currentRowErrors = rowErrors[index] || { dealerCode: '', repCodes: '' }
                                                                const currentTouched = touchedRows[index] || { dealer: false, reps: false }
                                                                const showDealerError = Boolean(currentRowErrors.dealerCode) && (forceRowErrors || currentTouched.dealer)
                                                                const pendingRepCodes = (row.repCodesInput || '')
                                                                    .split(/[\s,]+/)
                                                                    .map(code => code.trim())
                                                                    .filter(Boolean)
                                                                const showPendingRepError = pendingRepCodes.some(code => !REP_CODE_REGEX.test(code))
                                                                const showRepError = Boolean(currentRowErrors.repCodes)
                                                                    && (forceRowErrors || currentTouched.reps)
                                                                    && !(row.repCodesInput || '').trim()
                                                                const repErrorText = showPendingRepError
                                                                    ? translate('requestAccess.repCodesInvalid')
                                                                    : showRepError
                                                                        ? currentRowErrors.repCodes
                                                                        : ''

                                                                return (
                                                                    <Box key={row.id} className={classes.rowPanel}>
                                                                        <Box className={classes.rowFields}>
                                                                            <TextField
                                                                                fullWidth
                                                                                label={(
                                                                                    <Box className={classes.labelWithInfo}>
                                                                                        {translate('requestAccess.dealerCode')}
                                                                                        <Tooltip arrow classes={{ arrow: classes.registerTooltipArrow, tooltip: classes.registerTooltip }} title={translate('requestAccess.dealerCodeTooltip')} placement="right">
                                                                                            <InfoOutlinedIcon className={classes.infoIcon} />
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                )}
                                                                                placeholder={translate('requestAccess.dealerCodePlaceholder')}
                                                                                value={row.dealerCode}
                                                                                onChange={e => handleDealerChange(index, e.target.value)}
                                                                                onBlur={() => markDealerTouched(index)}
                                                                                inputProps={{ maxLength: 4 }}
                                                                                error={showDealerError || undefined}
                                                                                helperText={showDealerError ? currentRowErrors.dealerCode : ''}
                                                                                InputLabelProps={{ shrink: true }}
                                                                                variant="standard"
                                                                                className={classes.dealerInput}
                                                                            />
                                                                            <Autocomplete
                                                                                freeSolo
                                                                                multiple
                                                                                fullWidth
                                                                                label={(
                                                                                    <Box className={classes.labelWithInfo}>
                                                                                        {translate('requestAccess.repCodes')}
                                                                                        <Tooltip arrow classes={{ arrow: classes.registerTooltipArrow, tooltip: classes.registerTooltip }} title={translate('requestAccess.repCodesTooltip')} placement="right">
                                                                                            <InfoOutlinedIcon className={classes.infoIcon} />
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                )}
                                                                                placeholder={translate('requestAccess.repCodesPlaceholderShort')}
                                                                                options={[]}
                                                                                value={row.repCodes}
                                                                                onChange={(e, val) => handleRepChange(index, val)}
                                                                                inputValue={row.repCodesInput || ''}
                                                                                onInputChange={(e, val, reason) => handleRepInputChange(index, val, reason)}
                                                                                onBlur={() => handleRepBlur(index)}
                                                                                onKeyDown={e => handleRepKeyDown(e, index)}
                                                                                TextFieldProps={{
                                                                                    error: showRepError || showPendingRepError,
                                                                                    helperText: repErrorText,
                                                                                    InputLabelProps: { shrink: true }
                                                                                }}
                                                                                variant="standard"
                                                                                className={classes.repCodesInput}
                                                                            />
                                                                            {rows.length > 1 && (
                                                                                <IconButton size="small" onClick={() => handleRemoveRow(index)} aria-label={translate('requestAccess.removeDealerCode')} className={classes.removeRowButton}>
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                )
                                                            })}
                                                        </Box>

                                                        <Button
                                                            variant="outlined"
                                                            onClick={handleAddRow}
                                                            className={classes.addDealerButton}
                                                        >
                                                            <AddOutlinedIcon style={{ fontSize: 16, marginRight: 4 }} />
                                                            {translate('requestAccess.addDealerCode')}
                                                        </Button>
                                                    </>
                                                )}
                                            </Box>
                                        )
                                    })}
                                </Box>

                                {showStepOneBlockedMessage && (
                                    <Box className={classes.formBlockedMessage}>
                                        <InfoOutlinedIcon className={classes.formBlockedIcon} />
                                        <Typography className={classes.formBlockedText}>
                                            {translate('requestAccess.completeHighlightedFields')}
                                        </Typography>
                                    </Box>
                                )}

                                <Box className={classes.cardFooter}>
                                    <Button
                                        onClick={handleCancel}
                                        variant="text"
                                        className={classes.textPrimaryButton}
                                    >
                                        {translate('registrationAnalytics.cancel')}
                                    </Button>
                                    <Typography className={classes.selectedCountLabel}>
                                        {`(${selectedAppIds.length}) ${translate('requestAccess.selected')}`}
                                    </Typography>
                                    <Button
                                        disableElevation
                                        disableRipple
                                        disabled={selectedAppIds.length === 0}
                                        onClick={handleNext}
                                        className={classes.nextButton}
                                    >
                                        {translate('next')}
                                    </Button>
                                </Box>
                            </Paper>
                        )}

                        {stepNumber === 0 && page === 2 && (
                            <Paper className={classes.productContentContainer}>
                                <Box className={classes.cardHeader}>
                                    <Typography className={classes.title}>{stepTwoLabel}</Typography>
                                </Box>

                                <Form noValidate {...{
                                    initialValues: { ...memoizedInitialValues },
                                    onChange: val => {
                                        setFormInfoValues(val)
                                        updateVisibleFormErrors()
                                    },
                                    onSubmit: handleSubmit,
                                    ref,
                                    validate,
                                    validateOnBlur: false,
                                    validateOnChange: false,
                                }}>

                                    <Box className={classes.formContent}>
                                        <Typography className={classes.reviewSubtitle}>{translate('requestAccess.enterInformationVerbiage')}</Typography>
                                        <Grid container spacing={2} className={classes.formGrid}>
                                            <Grid item md={6} sm={6} xs={12}>
                                                <TextField {...{
                                                    field: FIRST_NAME.value,
                                                    fullWidth: true,
                                                    InputLabelProps: { className: classes.inputMataLabelProps },
                                                    InputProps: { onFocus: () => clearFieldError(FIRST_NAME.value), onBlur: () => {
                                                        const ctx = ref.current?.getContext?.()
                                                        if (ctx?.setFieldTouched) ctx.setFieldTouched(FIRST_NAME.value, true, false)
                                                        handleFieldBlur()
                                                    } },
                                                    label: translate('registrationAnalytics.firstname'),
                                                    placeholder: translate('registrationAnalytics.firstname'),
                                                    type: 'text'
                                                }} />
                                            </Grid>
                                            <Grid item className={isMobile ? classes.fieldPaddingTop : undefined} md={6} sm={6} xs={12}>
                                                <TextField {...{
                                                    field: LAST_NAME.value,
                                                    fullWidth: true,
                                                    InputLabelProps: { className: classes.inputMataLabelProps },
                                                    InputProps: { onFocus: () => clearFieldError(LAST_NAME.value), onBlur: () => {
                                                        const ctx = ref.current?.getContext?.()
                                                        if (ctx?.setFieldTouched) ctx.setFieldTouched(LAST_NAME.value, true, false)
                                                        handleFieldBlur()
                                                    } },
                                                    label: translate('registrationAnalytics.lastname'),
                                                    placeholder: translate('registrationAnalytics.lastname'),
                                                    type: 'text',
                                                }} />
                                            </Grid>
                                            <Grid item md={6} sm={6} xs={12} className={classes.fieldPaddingTop}>
                                                <TextField {...{
                                                    field: COMPANY_NAME.value,
                                                    fullWidth: true,
                                                    InputLabelProps: { className: classes.inputMataLabelProps },
                                                    InputProps: { onFocus: () => clearFieldError(COMPANY_NAME.value), onBlur: () => {
                                                        const ctx = ref.current?.getContext?.()
                                                        if (ctx?.setFieldTouched) ctx.setFieldTouched(COMPANY_NAME.value, true, false)
                                                        handleFieldBlur()
                                                    } },
                                                    label: translate('registrationAnalytics.companyname'),
                                                    placeholder: translate('registrationAnalytics.companyname'),
                                                    type: 'text'
                                                }} />
                                            </Grid>

                                            {/* Country Code + Phone + Ext */}
                                            <Grid item md={6} sm={6} xs={12} className={clsx(classes.fieldPaddingTop, classes.phoneRow)}>
                                                <Box className={classes.phoneCountryCol}>
                                                    <Autocomplete {...{
                                                        classes: {
                                                            listbox: classes.registerAutocompleteListbox,
                                                            option: classes.registerAutocompleteOption,
                                                            paper: classes.registerAutocompletePaper,
                                                            popper: classes.registerAutocompletePopper,
                                                        },
                                                        disableClearable: true,
                                                        field: COUNTRY_CODE.value,
                                                        fullWidth: true,
                                                        getOptionLabel: option => getOptionLabelList(option, countryCodeList),
                                                        label: translate('registrationAnalytics.countryCode'),
                                                        noOptionsText: translate(NO_DATA),
                                                        onChange: () => {
                                                            clearFieldError(COUNTRY_CODE.value)
                                                            const ctx = ref.current?.getContext?.()
                                                            if (ctx?.validateForm) {
                                                                setTimeout(() => ctx.validateForm().then(() => updateVisibleFormErrors()), 0)
                                                            }
                                                        },
                                                        onBlur: () => {
                                                            const ctx = ref.current?.getContext?.()
                                                            if (ctx?.setFieldTouched) ctx.setFieldTouched(COUNTRY_CODE.value, true, false)
                                                            setTimeout(handleFieldBlur, 0)
                                                        },
                                                        options: countryCodeList,
                                                        placeholder: translate('registrationAnalytics.countryCode'),
                                                        TextFieldProps: { InputProps: { autoComplete: 'off' } },
                                                        variant: 'standard'
                                                    }} />
                                                </Box>
                                                <Box className={classes.phoneNumberCol}>
                                                    <TextField {...{
                                                        autoComplete: 'off',
                                                        field: PHONE_NUMBER.value,
                                                        fullWidth: true,
                                                        InputProps: { onChange: handlePhoneNumberChange, onFocus: () => clearFieldError(PHONE_NUMBER.value), onBlur: () => {
                                                            const ctx = ref.current?.getContext?.()
                                                            if (ctx?.setFieldTouched) ctx.setFieldTouched(PHONE_NUMBER.value, true, false)
                                                            handleFieldBlur()
                                                        } },
                                                        label: translate('registrationAnalytics.phonenumber'),
                                                        placeholder: translate('registrationAnalytics.phonenumber'),
                                                        type: 'text'
                                                    }} />
                                                </Box>
                                                <Box className={classes.phoneExtCol}>
                                                    <TextField {...{
                                                        autoComplete: 'off',
                                                        field: EXT.value,
                                                        fullWidth: true,
                                                        InputProps: { inputProps: { maxLength: 32 }, onChange: handleExtChange },
                                                        label: translate('registrationAnalytics.ext'),
                                                        placeholder: translate('registrationAnalytics.ext')
                                                    }} />
                                                </Box>
                                            </Grid>

                                            <Grid item md={6} sm={6} xs={12} className={classes.fieldPaddingTop}>
                                                <TextField {...{
                                                    field: EMAIL.value,
                                                    fullWidth: true,
                                                    InputLabelProps: { className: classes.inputMataLabelProps },
                                                    InputProps: {
                                                        onFocus: () => {
                                                            emailFocusedRef.current = true
                                                            clearFieldError(EMAIL.value)
                                                        },
                                                        onBlur: () => {
                                                            emailFocusedRef.current = false
                                                            emailBlurredRef.current = true
                                                            const ctx = ref.current?.getContext?.()
                                                            if (ctx?.setFieldTouched) ctx.setFieldTouched(EMAIL.value, true, false)
                                                            handleFieldBlur()
                                                        }
                                                    },
                                                    label: translate('registrationAnalytics.email'),
                                                    placeholder: translate('registrationAnalytics.email'),
                                                    type: 'text'
                                                }} />
                                            </Grid>
                                            <Grid item md={6} sm={6} xs={12} className={classes.fieldPaddingTop}>
                                                <Autocomplete {...{
                                                    classes: {
                                                        listbox: classes.registerAutocompleteListbox,
                                                        option: classes.registerAutocompleteOption,
                                                        paper: classes.registerAutocompletePaper,
                                                        popper: classes.registerAutocompletePopper,
                                                    },
                                                    disableClearable: true,
                                                    field: LANGUAGE.value,
                                                    fullWidth: true,
                                                    getOptionLabel: option => getOptionLabel(option),
                                                    label: translate('registrationAnalytics.language'),
                                                    noOptionsText: translate(NO_DATA),
                                                    onChange: () => clearFieldError(LANGUAGE.value),
                                                    onBlur: () => {
                                                        const ctx = ref.current?.getContext?.()
                                                        if (ctx?.setFieldTouched) ctx.setFieldTouched(LANGUAGE.value, true, false)
                                                        setTimeout(handleFieldBlur, 0)
                                                    },
                                                    options: languageList,
                                                    placeholder: translate('registrationAnalytics.language'),
                                                    TextFieldProps: { InputLabelProps: { className: classes.inputMataLabelProps }, InputProps: { autoComplete: 'off' } },
                                                    variant: 'standard'
                                                }} />
                                            </Grid>

                                        </Grid>
                                        <p className={classes.errorText}>{ errorMessage ? translate('oktaApiResponse.' + `${errorMessage}`) : null}</p>
                                    </Box>

                                    {showFormBlockedMessage && (
                                        <Box className={classes.formBlockedMessage}>
                                            <InfoOutlinedIcon className={classes.formBlockedIcon} />
                                            <Typography className={classes.formBlockedText}>
                                                {translate('requestAccess.completeHighlightedFields')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Card footer - 64px */}
                                    <Box className={classes.formFooter}>
                                        <Button
                                            onClick={handleCancel}
                                            variant="text"
                                            className={classes.textPrimaryButton}
                                        >
                                            {translate('registrationAnalytics.cancel')}
                                        </Button>
                                        <Box className={classes.formActions}>
                                            <Button className={classes.back} onClick={handleBack} variant="outlined">
                                                {translate('registrationDefault.back')}
                                            </Button>
                                            <Button
                                                disableElevation
                                                disableRipple
                                                onClick={handleSubmitButtonClick}
                                                type="submit"
                                                className={classes.nextButton}
                                            >
                                                {translate('next')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Form>
                            </Paper>
                        )}

                        {stepNumber === 0 && page === 3 && (
                            <Paper className={classes.productContentContainer}>
                                <Box className={classes.cardHeader}>
                                    <Typography className={classes.title}>{stepThreeLabel}</Typography>
                                </Box>
                                <Box ref={cardBodyRef} className={classes.cardBody} data-inner-scroll>
                                    <Typography className={classes.reviewSubtitle}>
                                        {translate('requestAccess.reviewSubtitle')}
                                    </Typography>
                                    <Box className={classes.reviewHeader}>
                                        <Typography className={classes.reviewSelectedCount}>
                                            {`${translate('requestAccess.selectedProducts')} (${selectedAppIds.length})`}
                                        </Typography>
                                        <Button
                                            onClick={() => { setPage(1); setReturningFromEdit(true); setReCaptcha(null); setCaptchaError('') }}
                                            startIcon={<EditIcon />}
                                            variant="outlined"
                                            className={classes.editSelectionButton}
                                        >
                                            {translate('requestAccess.editSelection')}
                                        </Button>
                                    </Box>
                                    {applications
                                        .filter(app => selectedAppIds.includes(String(app.id)))
                                        .map(app => {
                                            const name = isFrench(selectedLanguage) ? (app.nameFr || app.name) : app.name
                                            const isSeApp = app.name === SE_APP_NAME
                                            const isMataApp = app.name === MATA_APP_NAME
                                            return (
                                                <Box key={app.id} className={classes.productCard}>
                                                    <Typography className={classes.productCardTitle}>{name}</Typography>
                                                    {(isFrench(selectedLanguage) ? (app.descriptionFr || app.description) : app.description) && (
                                                        <Typography className={classes.productCardDescription}>
                                                            {isFrench(selectedLanguage) ? (app.descriptionFr || app.description) : app.description}
                                                        </Typography>
                                                    )}
                                                    {isSeApp && rows.map(row => (
                                                        <Box key={row.id} className={classes.reviewDetailsPanel}>
                                                            <Box className={classes.reviewDetailCol}>
                                                                <Typography className={classes.reviewDetailLabel}>
                                                                    {translate('requestAccess.dealerCode')}
                                                                </Typography>
                                                                <Typography className={classes.reviewDetailValue}>
                                                                    {row.dealerCode}
                                                                </Typography>
                                                            </Box>
                                                            <Box className={classes.reviewDetailCol}>
                                                                <Typography className={classes.reviewDetailLabel}>
                                                                    {translate('requestAccess.repCodes')}
                                                                </Typography>
                                                                <Box className={classes.reviewRepChipRow}>
                                                                    {row.repCodes.map(code => (
                                                                        <Box key={code} className={classes.reviewRepChip}>{code}</Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                    {isMataApp && mataSelectedOptions.length > 0 && (
                                                        <Box className={classes.reviewDetailsPanel}>
                                                            <Box className={classes.reviewDetailCol}>
                                                                <Typography className={classes.reviewDetailLabel}>
                                                                    {translate('requestAccess.mataCapabilitiesReview')}
                                                                </Typography>
                                                                <Typography className={classes.reviewDetailValue}>
                                                                    {mataSelectedOptions.map(enOption => {
                                                                        const idx = (app.requestAccessData.values || []).indexOf(enOption)
                                                                        return isFrench(selectedLanguage) && idx >= 0
                                                                            ? ((app.requestAccessData.valuesFr || app.requestAccessData.values)[idx] || enOption)
                                                                            : enOption
                                                                    }).join(', ')}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )
                                        })}
                                    {siteKey && (
                                        <Box className={classes.recaptcha}>
                                            <ReCAPTCHA
                                                key={`${recaptchaKey}-${isDarkMode ? 'dark' : 'light'}`}
                                                ref={recaptchaRef}
                                                className={clsx(classes.recaptchaScaled, classes.recaptchaDarkMode)}
                                                sitekey={siteKey}
                                                theme="light"
                                                onChange={onReCaptchaHandle}
                                            />
                                            {captchaError && (
                                                <Typography className={classes.errorText}>{captchaError}</Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Box className={classes.cardFooter}>
                                    <Button
                                        onClick={handleCancel}
                                        variant="text"
                                        disabled={submitStatus === RequestSubmits.PENDING}
                                        className={classes.textPrimaryButton}
                                    >
                                        {translate('registrationAnalytics.cancel')}
                                    </Button>
                                    <Box className={classes.formActions}>
                                        <Button className={classes.back} onClick={handleBackFromReview} variant="outlined" disabled={submitStatus === RequestSubmits.PENDING}>
                                            {translate('registrationDefault.back')}
                                        </Button>
                                        <Button
                                            disableElevation
                                            disableRipple
                                            disabled={Boolean(errorMessage) || submitStatus === RequestSubmits.PENDING}
                                            onClick={handleFinalSubmit}
                                            className={classes.nextButton}
                                        >
                                            {translate('requestAccess.submit')}
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {stepNumber === 1 && <SubmitSuccess />}
                    </Grid>
                </Grid>
                </Box>
                <Box className={classes.footerWrapper}>
                    <Footer moveToTopRef={moveToTopRef} />
                </Box>
            </Box>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={cancelDialogOpen}
                onClose={handleCancelDialogBack}
                PaperProps={{ className: classes.cancelDialog }}
            >
                <DialogTitle className={classes.cancelDialogTitle}>
                    {translate('requestAccess.cancelRequestTitle')}
                    <IconButton
                        onClick={handleCancelDialogBack}
                        className={classes.cancelDialogCloseButton}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.cancelDialogContent}>
                    <Typography className={classes.cancelDialogBodyText}>
                        {translate('requestAccess.cancelRequestBody1')}
                    </Typography>
                    <Typography className={classes.cancelDialogBodyText}>
                        {translate('requestAccess.cancelRequestBody2')}
                    </Typography>
                    <Box className={classes.cancelDialogIllustration}>
                        <Box className={classes.cancelDialogIllustrationBack} />
                        <Box className={classes.cancelDialogIllustrationFront} />
                        <svg className={classes.cancelDialogXIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.3" fill="none" />
                            <line x1="8.2" y1="8.2" x2="15.8" y2="15.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                            <line x1="15.8" y1="8.2" x2="8.2" y2="15.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                        </svg>
                    </Box>
                </DialogContent>
                <DialogActions className={classes.cancelDialogActions}>
                    <Button
                        variant="outlined"
                        onClick={handleCancelDialogBack}
                        className={classes.cancelDialogBackButton}
                    >
                        {translate('requestAccess.cancelRequestBack')}
                    </Button>
                    <Button
                        onClick={handleCancelDialogConfirm}
                        className={classes.cancelDialogConfirmButton}
                    >
                        {translate('requestAccess.cancelRequestConfirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )

}

Component.displayName = 'DefaultRegister'

Component.defaultProps = {
    stepNumber: 0,
}

Component.propTypes = {
    classes: PropTypes.shape({
        loader: PropTypes.string
    }),
    onAddNotification: PropTypes.func.isRequired,
    onRequestPhoneCodes: PropTypes.func.isRequired,
    onRequestRecaptcha: PropTypes.func.isRequired,
    onSubmitRequest: PropTypes.func.isRequired,
    recaptchaValue: PropTypes.object,
    stepNumber: PropTypes.number,
    submitStatus: PropTypes.string,
}

export default withStyles(Component, styles)
